import React from 'react';
import { UploadCloudIcon, ImageIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import BackButton from '../../components/UI/BackButton';
import { useEventManager } from './event-manager/useEventManager';
import { EventBasicsSection } from './event-manager/EventBasicsSection';
import { DateTimeSection } from './event-manager/DateTimeSection';
import { LocationSection } from './event-manager/LocationSection';
import { AccessRulesSection } from './event-manager/AccessRulesSection';
import { CapacitySection } from './event-manager/CapacitySection';
import { VisibilitySection } from './event-manager/VisibilitySection';
import { ConfirmationScreen } from './product-manager/ConfirmationScreen'; // Reusing existing confirmation screen
const EventManager = () => {
  const em = useEventManager();
  const dk = useDarkClasses();
  if (em.showConfirmation) {
    // We can reuse the ProductManager confirmation screen by adapting the props slightly
    // It expects a pm object, we'll pass a mock one that satisfies its needs
    const mockPm = {
      productType: 'event',
      mode: em.mode,
      savedAsDraft: em.savedAsDraft,
      darkMode: em.darkMode,
      navigate: em.navigate,
      handleTypeSelect: () => em.navigate('/vendors/dashboard')
    } as any;
    return <div className={`min-h-screen pt-20 pb-8 ${dk.page}`} data-id="element-18660" data-mp="w26z60skeqjn">
        <div className="max-w-3xl mx-auto px-4" data-id="element-18661" data-mp="r45i1lep91at">
          <ConfirmationScreen pm={mockPm} data-id="element-18662" data-mp="r4xc2rsneqyz" />
        </div>
      </div>;
  }
  return <div className={`min-h-screen pt-20 md:pt-6 pb-6 sm:pt-20 sm:pb-8 md:pt-8 ${dk.page} ${dk.textPrimary}`} data-id="element-18663" data-mp="mcdzny5p61vr">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18664" data-mp="hevm1ugqiyhf">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-18665" data-mp="de1yx3b49npl">
          <div className="flex items-center gap-4" data-id="element-18666" data-mp="8ccnhmjzf80z">
            <BackButton data-id="element-18667" data-mp="3ekv6rffdjw9" />
            <div data-id="element-18668" data-mp="3eb410in96oz">
              <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-18669" data-mp="3fw0ivoiqzph">
                {em.mode === 'create' ? 'Create New Event' : 'Edit Event'}
              </h1>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-18670" data-mp="3uv6bqund72lp">
                Submit structured event details for readers and vendors.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-18671" data-mp="3kx9zfzioy1m0">
            <button onClick={em.handleSaveDraft} disabled={em.isProcessing} className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${em.darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-18672" data-mp="3ky1m2kub8p05">
              Save Draft
            </button>
            <button onClick={em.handlePublish} disabled={em.isProcessing} className={`px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors ${em.isProcessing ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`} data-id="element-18673" data-mp="7pyicxq90rki">
              {em.isProcessing ? 'Publishing...' : 'Publish Event'}
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-18674" data-mp="2fw8yi7znf498">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6" data-id="element-18675" data-mp="2up2gc02e7wh2">
            <EventBasicsSection em={em} data-id="element-18676" data-mp="2pphhrqr4xq4v" />
            <DateTimeSection em={em} data-id="element-18677" data-mp="2pqitzyjw0io6" />
            <LocationSection em={em} data-id="element-18678" data-mp="21xskrx3dcdhl" />
            <AccessRulesSection em={em} data-id="element-18679" data-mp="21yu9339eawh9" />
          </div>

          {/* Right Column - Sidebar Info */}
          <div className="space-y-6" data-id="element-18680" data-mp="1x1bzdank0d87">
            <CapacitySection em={em} data-id="element-18681" data-mp="2az0ee1uxwq5c" />
            <VisibilitySection em={em} data-id="element-18682" data-mp="2b01wgqadicwk" />

            {/* Media Upload (Simplified inline version of MediaUploadSection) */}
            <div className={`p-6 rounded-xl shadow-sm ${dk.card}`} data-id="element-18683" data-mp="26zrq3cio5kh6">
              <h2 className={`text-lg font-bold mb-4 flex items-center ${dk.textHeading}`} data-id="element-18684" data-mp="3md48d81j2eae">
                <ImageIcon className="w-5 h-5 mr-2 text-purple-500" data-id="element-18685" data-mp="3icka2157x4mo" />
                Event Images
              </h2>

              <div className="space-y-4" data-id="element-18686" data-mp="hjz11ab63bi1">
                <div data-id="element-18687" data-mp="lk99quqcgg0l">
                  <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18688" data-mp="ljq001aukxxn">
                    Cover Image
                  </label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${em.darkMode ? 'border-gray-600 hover:border-purple-500 bg-gray-800/50' : 'border-gray-300 hover:border-purple-500 bg-gray-50'}`} data-id="element-18689" data-mp="vi6uv9cwpwoz">
                    <UploadCloudIcon className={`w-8 h-8 mx-auto mb-2 ${dk.textFaint}`} data-id="element-18690" data-mp="199ttvgy8hjbg" />
                    <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18691" data-mp="1e73ulsr2phel">
                      Click to upload
                    </p>
                    <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-18692" data-mp="1o5t7t880ydoi">
                      Square or 4:3 recommended
                    </p>
                  </div>
                </div>

                <div data-id="element-18693" data-mp="33p0mbhhgbzir">
                  <label className={`block text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-18694" data-mp="33ohfowx94xk1">
                    Banner Image (Optional)
                  </label>
                  <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${em.darkMode ? 'border-gray-600 hover:border-purple-500 bg-gray-800/50' : 'border-gray-300 hover:border-purple-500 bg-gray-50'}`} data-id="element-18695" data-mp="2trcuazb8d64a">
                    <UploadCloudIcon className={`w-8 h-8 mx-auto mb-2 ${dk.textFaint}`} data-id="element-18696" data-mp="3hoidu4m4tqvt" />
                    <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-18697" data-mp="3coxgfymm1ql7">
                      Click to upload
                    </p>
                    <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-18698" data-mp="e1jiojbeh7q3">
                      16:9 or wider recommended
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default EventManager;